import { Geolocation, Position, PositionOptions } from "@capacitor/geolocation";
import { API_KEY } from "./google_maps_api";
import { HttpRequest, InternetConnectionError } from "./HttpRequest";
import * as messages from "./messages";
import { Geo } from "./messages";
import { getLocalStorageItem, setLocalStorageItem } from "./storage_utils";

type LatLngTuple = [number, number];

export const HAS_PROMPTED_WITH_LOCATION_ADDRESS = "has_prompted_with_location_access";

export async function getLocationPermissionState() {
  if (!("permissions" in navigator)) {
    if ((await getLocalStorageItem(HAS_PROMPTED_WITH_LOCATION_ADDRESS)) === null) {
      setLocalStorageItem(HAS_PROMPTED_WITH_LOCATION_ADDRESS, "1");
      return "prompt_manually" as const;
    }

    return "unknown" as const;
  }

  try {
    const { location: state } = await Geolocation.checkPermissions();
    return state;
  } catch (err) {
    console.error(err);
    return "unknown" as const;
  }
}

async function getLocation(options?: PositionOptions): Promise<Position> {
  return Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 5000, ...options });
}

async function getGeoAndAdressOrNull(options?: PositionOptions) {
  return getLocation(options).then(getGeoAndAddressByPosition);
}

async function getGeoAndAddress(options?: PositionOptions) {
  const geoAndAddress = await getGeoAndAdressOrNull(options);

  if (geoAndAddress === null) {
    throw new Error(`Expected to receive geo and address, but received ${geoAndAddress} instead`);
  }

  return geoAndAddress;
}

export async function getGeoAndAddressByPosition(
  position: Position
): Promise<messages.GeoAndAddress | null> {
  console.log("[geolocation:get]", position.coords);
  const geo = {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
  };
  const method = "GET";
  const baseUrl = "https://maps.googleapis.com/maps/api/geocode/json";
  const endpoint = `?latlng=${geo.lat},${geo.lng}&key=${API_KEY}`;
  const url = `${baseUrl}${endpoint}`;
  const headers: [string, string][] = [];
  const body = null;
  const req = new HttpRequest(method, url, headers, body);
  let rsp: XMLHttpRequest;

  try {
    rsp = await req.fetch();
  } catch (err) {
    if (err instanceof InternetConnectionError) {
      return null;
    } else {
      throw err;
    }
  }
  if (rsp.status >= 200 && rsp.status < 300) {
    // TODO validate response against JSON schema
    const result: any = JSON.parse(rsp.responseText);
    return {
      address: result.results[0].formatted_address,
      geoLocation: {
        lat: geo.lat,
        lng: geo.lng,
      },
    };
  } else {
    return null;
  }
}

/**
 * This function has the exact logic as clock in/out of range issue in the server
 */
export function getDistanceInFeet(visitLocation: Geo, caregiverLocation: Geo) {
  return (
    ((2 * 6371 * 5280) / 1.609344) *
    Math.atan2(
      Math.sqrt(
        Math.pow(Math.sin(((Math.PI / 180) * (visitLocation.lat - caregiverLocation.lat)) / 2), 2) +
          Math.pow(
            Math.sin(((Math.PI / 180) * (visitLocation.lng - caregiverLocation.lng)) / 2),
            2
          ) *
            Math.cos((Math.PI / 180) * caregiverLocation.lat) *
            Math.cos((Math.PI / 180) * visitLocation.lat)
      ),
      Math.sqrt(
        1 -
          (Math.pow(Math.sin(((Math.PI / 180) * (visitLocation.lat - visitLocation.lat)) / 2), 2) +
            Math.pow(
              Math.sin(((Math.PI / 180) * (visitLocation.lng - caregiverLocation.lng)) / 2),
              2
            ) *
              Math.cos((Math.PI / 180) * caregiverLocation.lat) *
              Math.cos((Math.PI / 180) * visitLocation.lat))
      )
    )
  );
}

export function getDistanceInMeters(visitLocation: Geo, caregiverLocation: Geo): number {
  // Radius of the Earth in meters
  const R = 6371e3;

  // convert latitude from degrees to radians
  const lat1 = (visitLocation.lat * Math.PI) / 180;
  const lat2 = (caregiverLocation.lat * Math.PI) / 180;

  // difference of latitude
  const deltaLat = ((caregiverLocation.lat - visitLocation.lat) * Math.PI) / 180;

  // difference of longitude
  const deltaLng = ((caregiverLocation.lng - visitLocation.lng) * Math.PI) / 180;

  // Haversine formula
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Distance in meters
  const distance = R * c;

  return distance;
}

/**
 * Decodes an encoded path string into a sequence of LatLngs.
 *
 * See {@link https://developers.google.com/maps/documentation/utilities/polylinealgorithm}
 *
 *  #### Example
 *
 * ```js
 * import { decode } from "@googlemaps/polyline-codec";
 *
 * const encoded = "_p~iF~ps|U_ulLnnqC_mqNvxq`@";
 * console.log(decode(encoded, 5));
 * // [
 * //   [38.5, -120.2],
 * //   [40.7, -120.95],
 * //   [43.252, -126.453],
 * // ]
 * ```
 */
export const decodePolylineString = function (encodedPath: string, precision = 5): LatLngTuple[] {
  const factor = Math.pow(10, precision);

  const len = encodedPath.length;

  // For speed we preallocate to an upper bound on the final length, then
  // truncate the array before returning.
  const path = new Array(Math.floor(encodedPath.length / 2));
  let index = 0;
  let lat = 0;
  let lng = 0;
  let pointIndex = 0;

  // This code has been profiled and optimized, so don't modify it without
  // measuring its performance.
  for (; index < len; ++pointIndex) {
    // Fully unrolling the following loops speeds things up about 5%.
    let result = 1;
    let shift = 0;
    let b: number;
    do {
      // Invariant: "result" is current partial result plus (1 << shift).
      // The following line effectively clears this bit by decrementing "b".
      b = encodedPath.charCodeAt(index++) - 63 - 1;
      result += b << shift;
      shift += 5;
    } while (b >= 0x1f); // See note above.
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    result = 1;
    shift = 0;
    do {
      b = encodedPath.charCodeAt(index++) - 63 - 1;
      result += b << shift;
      shift += 5;
    } while (b >= 0x1f);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    path[pointIndex] = [lat / factor, lng / factor];
  }
  // truncate array
  path.length = pointIndex;

  return path;
};

const locationUtils = {
  getLocation: getLocation,
  getGeoAndAdressOrNull: getGeoAndAdressOrNull,
  getGeoAndAddress: getGeoAndAddress,
  getLocationPermissionState: getLocationPermissionState,
};

export default locationUtils;
